import { memo, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertTriangle,
  ArrowDown,
  Bot,
  Bug,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RotateCcw,
  Search,
  Send,
  Square,
  Timer,
  Wrench,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useLogDebugger } from '@/hooks/use-log-debugger';
import type { ChatMessage, ToolCallInfo } from '@/types/log-debugger';

type TimeRangePreset = '1h' | '6h' | '24h' | '7d';

const TIME_RANGE_LABELS: Record<TimeRangePreset, string> = {
  '1h': 'Last 1 hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
};

const SUGGESTIONS = [
  {
    icon: AlertTriangle,
    title: 'Recent errors',
    prompt: 'Show me the most frequent errors from the last hour and group them by root cause.',
  },
  {
    icon: Search,
    title: 'Trace a user',
    prompt: 'Find logs for user john@example.com and summarize what went wrong.',
  },
  {
    icon: Bug,
    title: 'Endpoint failures',
    prompt: "What's causing the 500 errors on the orders endpoint?",
  },
  {
    icon: KeyRound,
    title: 'Auth issues',
    prompt: 'Investigate Cognito login failures from today and identify affected accounts.',
  },
] as const;

function getTimeRange(preset: TimeRangePreset): { start: string; end: string } {
  const now = new Date();
  const offsets: Record<TimeRangePreset, number> = {
    '1h': 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
  };
  return {
    start: new Date(now.getTime() - offsets[preset]).toISOString(),
    end: now.toISOString(),
  };
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * GFM tables from the model are unpredictable in shape, so they render in a
 * horizontally scrollable container with top-aligned, left-aligned cells
 * instead of the default prose table styles.
 */
const markdownComponents: Components = {
  table: ({ children }) => (
    <div className="not-prose my-3 overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
  tr: ({ children }) => <tr className="border-b last:border-b-0">{children}</tr>,
  th: ({ children }) => (
    <th className="whitespace-nowrap px-3 py-2 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-left align-top">{children}</td>,
};

/**
 * Markdown is memoized per content string so older messages never re-parse
 * while the newest message streams in.
 */
const MarkdownContent = memo(function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:mb-2 prose-headings:mt-4 first:prose-headings:mt-0 prose-pre:my-2 prose-pre:bg-ink-black/95 prose-code:text-xs prose-code:before:content-none prose-code:after:content-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
});

const UserBubble = memo(function UserBubble({ message }: { message: ChatMessage }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-sm">
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
      <span className="pr-1 text-[11px] text-muted-foreground">{formatTime(message.timestamp)}</span>
    </div>
  );
});

function ToolCallStep({ toolCall }: { toolCall: ToolCallInfo }) {
  const [expanded, setExpanded] = useState(false);
  const hasInput = Object.keys(toolCall.input ?? {}).length > 0;
  const failed = toolCall.resultSummary === 'The tool could not complete this request.';
  return (
    <div className="rounded-lg border bg-muted/40 text-xs">
      <button
        type="button"
        onClick={() => hasInput && setExpanded(previous => !previous)}
        className={cn(
          'flex w-full items-center gap-2 px-2.5 py-1.5 text-left',
          hasInput && 'hover:bg-muted/70'
        )}
        aria-expanded={expanded}
      >
        {toolCall.isActive ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
        ) : failed ? (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
        ) : toolCall.resultSummary ? (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
        ) : (
          <Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="font-medium capitalize">{toolCall.name.replace(/_/g, ' ')}</span>
        {toolCall.resultSummary && (
          <span className="truncate text-muted-foreground">{toolCall.resultSummary}</span>
        )}
        {hasInput && (
          <ChevronRight
            className={cn(
              'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90'
            )}
          />
        )}
      </button>
      {expanded && hasInput && (
        <pre className="overflow-x-auto border-t bg-muted/60 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {JSON.stringify(toolCall.input, null, 2)}
        </pre>
      )}
    </div>
  );
}

function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard access can be denied; the button simply stays idle.
      });
  }, [content]);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
      onClick={copy}
      aria-label="Copy response"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

const AssistantMessage = memo(function AssistantMessage({ message }: { message: ChatMessage }) {
  const segments = message.segments ?? [];
  const hasSegments = segments.length > 0;
  if (!hasSegments && !message.content) return null;
  return (
    <div className="group flex flex-col items-start gap-1">
      <div className="flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
          <Bot className="h-3.5 w-3.5 text-primary" />
        </div>
        <span className="text-xs font-medium text-muted-foreground">Assistant</span>
        <span className="text-[11px] text-muted-foreground/70">{formatTime(message.timestamp)}</span>
        {message.content && <CopyButton content={message.content} />}
      </div>
      <div className="w-full min-w-0 space-y-2 pl-8">
        {hasSegments ? (
          segments.map((segment, index) =>
            segment.type === 'tool_call' ? (
              <ToolCallStep key={segment.toolCall.toolUseId} toolCall={segment.toolCall} />
            ) : segment.content ? (
              <MarkdownContent key={`text-${index}`} content={segment.content} />
            ) : null
          )
        ) : (
          <MarkdownContent content={message.content} />
        )}
      </div>
    </div>
  );
});

function WorkingIndicator({ toolCall }: { toolCall: ToolCallInfo | null }) {
  return (
    <div className="flex items-center gap-2 pl-8">
      <div className="flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 shadow-sm">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span className="animate-pulse text-xs text-muted-foreground">
          {toolCall ? `Running ${toolCall.name.replace(/_/g, ' ')}…` : 'Thinking…'}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ onSuggestion }: { onSuggestion: (prompt: string) => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-4 py-12">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
        <Bot className="h-7 w-7 text-primary" />
      </div>
      <div className="max-w-md text-center">
        <h2 className="text-lg font-semibold">What can I dig into for you?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          I investigate CloudWatch logs, Cognito accounts, database users, and orders. Ask in plain
          English — I will pick the right tools.
        </p>
      </div>
      <div className="grid w-full max-w-xl grid-cols-1 gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map(suggestion => (
          <button
            key={suggestion.title}
            type="button"
            onClick={() => onSuggestion(suggestion.prompt)}
            className="flex items-start gap-3 rounded-xl border bg-card p-3 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/50"
          >
            <suggestion.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              <span className="block text-sm font-medium">{suggestion.title}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">
                {suggestion.prompt}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function LogDebuggerPage() {
  const {
    messages,
    isStreaming,
    activeToolCall,
    error,
    tokensUsed,
    sendMessage,
    clearConversation,
    stopStreaming,
  } = useLogDebugger();
  const [input, setInput] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRangePreset>('24h');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = scrollContainerRef.current;
    container?.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    nearBottomRef.current = distance < 120;
    setShowScrollButton(distance >= 120);
  }, []);

  // Follow the stream only while the reader is already at the bottom, so
  // scrolling up to read earlier output is never hijacked.
  useEffect(() => {
    if (nearBottomRef.current) scrollToBottom(isStreaming ? 'auto' : 'smooth');
  }, [messages, isStreaming, error, scrollToBottom]);

  const resizeTextarea = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`;
  }, []);

  const submit = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;
      setInput('');
      requestAnimationFrame(resizeTextarea);
      nearBottomRef.current = true;
      sendMessage(trimmed, { timeRange: getTimeRange(timeRange) });
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [isStreaming, resizeTextarea, sendMessage, timeRange]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(input);
    }
  };

  const lastUserMessage = [...messages].reverse().find(message => message.role === 'user');

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-white p-4 sm:p-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Log Debugger</h1>
          <p className="text-sm text-muted-foreground">AI-powered CloudWatch log analysis</p>
        </div>
        <div className="flex items-center gap-2">
          {tokensUsed > 0 && (
            <Badge variant="secondary" className="hidden font-normal tabular-nums sm:inline-flex">
              {tokensUsed.toLocaleString()} tokens
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={clearConversation}
            className="gap-2"
            disabled={messages.length === 0 && !error}
          >
            <Plus className="h-4 w-4" />
            New Chat
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto p-4 sm:p-6 lg:px-8"
        >
          {messages.length === 0 ? (
            <EmptyState onSuggestion={submit} />
          ) : (
            <div className="mx-auto max-w-3xl space-y-5 pb-4">
              {messages.map(message =>
                message.role === 'user' ? (
                  <UserBubble key={message.id} message={message} />
                ) : (
                  <AssistantMessage key={message.id} message={message} />
                )
              )}
              {isStreaming && <WorkingIndicator toolCall={activeToolCall} />}
              {error && (
                <div className="flex max-w-[85%] items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                    {lastUserMessage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 gap-1.5"
                        onClick={() => submit(lastUserMessage.content)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {showScrollButton && (
          <Button
            variant="outline"
            size="icon"
            className="absolute bottom-4 left-1/2 h-8 w-8 -translate-x-1/2 rounded-full shadow-md"
            onClick={() => {
              nearBottomRef.current = true;
              setShowScrollButton(false);
              scrollToBottom();
            }}
            aria-label="Scroll to latest"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Composer */}
      <div className="border-t bg-white p-4 sm:p-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-xl border bg-card shadow-sm focus-within:ring-1 focus-within:ring-ring">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={event => {
                setInput(event.target.value);
                resizeTextarea();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your logs…"
              className="max-h-[160px] min-h-[44px] resize-none border-0 shadow-none focus-visible:ring-0"
              rows={1}
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
              <Select value={timeRange} onValueChange={(value: TimeRangePreset) => setTimeRange(value)}>
                <SelectTrigger className="h-8 w-auto gap-1.5 border-0 bg-transparent text-xs text-muted-foreground shadow-none hover:text-foreground">
                  <Timer className="h-3.5 w-3.5" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TIME_RANGE_LABELS) as TimeRangePreset[]).map(preset => (
                    <SelectItem key={preset} value={preset}>
                      {TIME_RANGE_LABELS[preset]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <span className="hidden text-[11px] text-muted-foreground sm:inline">
                  Enter to send · Shift+Enter for a new line
                </span>
                {isStreaming ? (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={stopStreaming}
                    aria-label="Stop streaming"
                  >
                    <Square className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => submit(input)}
                    disabled={!input.trim()}
                    aria-label="Send message"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
